import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ExternalLink, Sparkles, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import {
  createOffice,
  getMpzpOfficeMatches,
  lookupMpzpOffice,
  type JstOfficeProposal,
  type JstOfficeSuggestion,
} from "../../api";
import { useMpzpOrderMutations, useSelectableOffices, useTaskPolling } from "../../hooks";
import { apiErr } from "../../utils";
import { TaskLog } from "../ui";
import { MpzpCoverageBadge } from "./MpzpCoverageBadge";
import type { MpzpCandidateGroup, MpzpCandidateParcel, WypisApplicant } from "../../types";

const APPLICANT_OPTIONS: { value: WypisApplicant; label: string }[] = [
  { value: "elektim", label: "ELEKTIM" },
  { value: "energa", label: "ENERGA" },
  { value: "tauron", label: "TAURON" },
];

/**
 * Jedno zamówienie = jedna gmina. Modal otwiera się z grupy gminnej w panelu
 * kandydatów, z zaznaczonymi działkami — tutaj można je już tylko usuwać.
 */
export function CreateMpzpOrderModal({
  group,
  parcels,
  onClose,
}: {
  group: MpzpCandidateGroup;
  parcels: MpzpCandidateParcel[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const { data: offices } = useSelectableOffices();
  const { data: users } = useInternalUsers();
  const { create } = useMpzpOrderMutations();

  const [selected, setSelected] = useState<MpzpCandidateParcel[]>(parcels);
  const [officeId, setOfficeId] = useState("");
  const [applicant, setApplicant] = useState<WypisApplicant>(
    (parcels[0]?.operator as WypisApplicant) === "tauron" ? "tauron" : "energa",
  );
  const [contactPerson, setContactPerson] = useState(me?.id ? String(me.id) : "");
  const [wantWypis, setWantWypis] = useState(true);
  const [wantWyrys, setWantWyrys] = useState(true);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  // Urzędy gminy z rejestru dopasowane do tej gminy — węższe i trafniejsze niż pełna
  // lista urzędów, którą i tak zostawiamy jako fallback.
  const { data: matches } = useQuery({
    queryKey: ["pp", "mpzp-office-matches", group.commune],
    queryFn: () =>
      getMpzpOfficeMatches({
        commune: group.commune,
        county: group.county,
        voivodeship: group.voivodeship,
      }),
    enabled: !!group.commune,
  });

  useEffect(() => {
    if (!officeId && matches?.matches.length === 1) setOfficeId(matches.matches[0].id);
  }, [matches, officeId]);

  // ── wyszukiwanie AI, gdy rejestr nie ma tej gminy ──
  // Ta sama ścieżka co przy starostwach na wypisach EGiB: propozycja z internetu →
  // użytkownik ją ogląda i zatwierdza → DOPIERO wtedy urząd trafia do rejestru.
  // Nic nie zapisuje się automatycznie: zły adres na wniosku to zmarnowany miesiąc.
  const qc = useQueryClient();
  const [lookupTaskId, setLookupTaskId] = useState<string | null>(null);
  const { data: lookupTask } = useTaskPolling(lookupTaskId);
  const [lookupError, setLookupError] = useState("");

  const lookup = useMutation({
    mutationFn: () =>
      lookupMpzpOffice({
        commune: group.commune,
        county: group.county,
        voivodeship: group.voivodeship,
        teryts: selected.map((p) => p.teryt).filter(Boolean).slice(0, 20),
      }),
    onSuccess: (r) => {
      setLookupError("");
      setLookupTaskId(r.task_id);
    },
    onError: () => setLookupError("Nie udało się uruchomić wyszukiwania."),
  });

  const searching =
    !!lookupTaskId && !["success", "failure", "cancelled"].includes(lookupTask?.status ?? "");
  const suggestion =
    lookupTask?.status === "success" ? (lookupTask.result as JstOfficeSuggestion | null) : null;
  const foundInRegistry = suggestion?.existing ?? [];

  // Propozycja AI trafia do stanu, bo jest EDYTOWALNA — model potrafi pomylić urząd
  // gminy z urzędem miasta o tej samej nazwie albo podać konto innego wydziału, a
  // poprawianie tego później w rejestrze urzędów jest o krok za późno.
  const [proposal, setProposal] = useState<JstOfficeProposal | null>(null);
  useEffect(() => {
    if (suggestion?.proposal) setProposal(suggestion.proposal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupTask?.status]);

  const setProposalField = (k: keyof JstOfficeProposal, v: string) =>
    setProposal((p) => (p ? { ...p, [k]: v } : p));

  // Urząd znaleziony w rejestrze w trakcie wyszukiwania — wybieramy go od razu,
  // bo tworzenie duplikatu byłoby czystą stratą.
  useEffect(() => {
    if (foundInRegistry.length && !officeId) setOfficeId(foundInRegistry[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupTask?.status]);

  const acceptProposal = useMutation({
    mutationFn: () =>
      createOffice({
        name: proposal!.name,
        kind: "gmina",
        department: proposal!.department,
        street: proposal!.street,
        building_no: proposal!.building_no,
        postal_code: proposal!.postal_code,
        city: proposal!.city,
        phone: proposal!.phone,
        email: proposal!.email,
        bank_account: proposal!.bank_account,
        // Źródło zostaje w notatkach urzędu — po miesiącach to jedyny ślad, skąd
        // wzięły się te dane i czy warto im ufać.
        notes: [
          proposal!.source ? `Dane ustalone przez AI, źródło: ${proposal!.source}` : "",
          proposal!.bank_account_source ? `Konto: ${proposal!.bank_account_source}` : "",
          proposal!.website,
        ]
          .filter(Boolean)
          .join("\n"),
      }),
    onSuccess: (office) => {
      qc.invalidateQueries({ queryKey: ["pp", "offices"] });
      qc.invalidateQueries({ queryKey: ["pp", "mpzp-office-matches", group.commune] });
      setOfficeId(office.id);
      setLookupTaskId(null);
    },
    onError: () => setLookupError("Nie udało się zapisać urzędu w rejestrze."),
  });

  const officeOptions = useMemo(() => {
    const suggested = (matches?.matches ?? []).map((o) => ({
      value: o.id,
      label: `★ ${o.name}${o.full_address ? ` — ${o.full_address}` : ""}`,
    }));
    const suggestedIds = new Set((matches?.matches ?? []).map((o) => o.id));
    const rest = (offices ?? [])
      .filter((o) => o.kind === "gmina" && !suggestedIds.has(o.id))
      .map((o) => ({ value: o.id, label: o.name }));
    return [...suggested, ...rest];
  }, [matches, offices]);

  // Działki bez potwierdzonego pokrycia planem — nie blokują zamówienia (gmina wyda
  // zaświadczenie o braku planu), ale użytkownik ma to zobaczyć zanim wyśle.
  const withoutPlan = selected.filter((p) => p.mpzp_status !== "covered");
  const missingAddress = selected.filter((p) => p.missing_address);

  const submit = async () => {
    setError("");
    if (!officeId) {
      setError("Wskaż urząd gminy/miasta — to on wydaje wypis i wyrys z planu.");
      return;
    }
    if (!selected.length) {
      setError("Zamówienie musi obejmować co najmniej jedną działkę.");
      return;
    }
    try {
      const order = await create.mutateAsync({
        parcel_ids: selected.map((p) => p.id),
        commune: group.commune,
        commune_teryt: group.commune_teryt,
        county: group.county,
        voivodeship: group.voivodeship,
        office: officeId,
        applicant,
        contact_person: contactPerson || undefined,
        want_wypis: wantWypis,
        want_wyrys: wantWyrys,
        notes,
      });
      navigate(`/power-projects/mpzp/${order.id}`);
    } catch (e) {
      setError(apiErr(e, "Nie udało się utworzyć zamówienia."));
    }
  };

  return (
    <Modal open onClose={onClose} title={`Zamów wypis i wyrys — gmina ${group.commune}`} size="lg">
      <div className="space-y-4">
        {/* ── zakres ── */}
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="checkbox" checked={wantWypis} onChange={() => setWantWypis((v) => !v)} />
            Wypis z planu
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="checkbox" checked={wantWyrys} onChange={() => setWantWyrys((v) => !v)} />
            Wyrys z planu
          </label>
        </div>

        {/* ── adresat ── */}
        <div>
          <label className="label-base">Urząd gminy/miasta (adresat)</label>
          <SearchableSelect
            value={officeId}
            onChange={(e) => setOfficeId(e.target.value)}
            options={officeOptions}
            placeholder="Wybierz urząd…"
          />
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Button
              size="sm"
              variant="secondary"
              loading={lookup.isPending || searching}
              onClick={() => lookup.mutate()}
            >
              <Sparkles size={13} className="mr-1" />
              Znajdź urząd (AI)
            </Button>
            {!officeOptions.length && !searching && !proposal && (
              <span className="text-[11px] text-amber-700">
                Rejestr nie ma urzędu gminy {group.commune} — poszukaj go w internecie.
              </span>
            )}
          </div>

          {searching && (
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
                <Spinner /> Szukam urzędu gminy {group.commune}…
              </div>
              <TaskLog log={lookupTask?.log ?? ""} />
            </div>
          )}

          {lookupTask?.status === "success" && !proposal && !foundInRegistry.length && (
            <p className="text-[11px] text-amber-700 mt-1">
              Nie udało się ustalić urzędu — dodaj go ręcznie w rejestrze urzędów.
            </p>
          )}

          {foundInRegistry.length > 0 && (
            <p className="text-[11px] text-green-700 mt-1">
              Urząd był już w rejestrze: <b>{foundInRegistry[0].name}</b> — wybrany powyżej.
            </p>
          )}

          {proposal && (
            <div className="mt-2 border border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)] p-2 space-y-2 text-xs">
              <div className="text-[var(--color-text-secondary)]">
                Propozycja AI — <b>sprawdź i popraw przed zapisem</b>. Model potrafi pomylić
                urząd gminy z urzędem miasta o tej samej nazwie, a zły adresat to zmarnowany
                miesiąc oczekiwania.
              </div>
              {/* Link do źródła, żeby dało się zweryfikować adres niezależnie. */}
              {(proposal.contact_url || proposal.website || proposal.source) && (
                <a
                  href={proposal.contact_url || proposal.website || proposal.source}
                  target="_blank"
                  rel="noreferrer"
                  title={proposal.contact_url || proposal.website || proposal.source}
                  className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
                >
                  <ExternalLink size={11} /> Sprawdź adres i kontakt na stronie urzędu
                </a>
              )}
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="section-label">Nazwa urzędu *</span>
                  <input className="input-base mt-0.5 text-xs" value={proposal.name}
                    onChange={(e) => setProposalField("name", e.target.value)} />
                </label>
                <label className="block">
                  <span className="section-label">Wydział (planowanie przestrzenne)</span>
                  <input className="input-base mt-0.5 text-xs" value={proposal.department}
                    placeholder="np. Wydział Urbanistyki i Architektury"
                    onChange={(e) => setProposalField("department", e.target.value)} />
                </label>
                <label className="block">
                  <span className="section-label">Ulica *</span>
                  <input className="input-base mt-0.5 text-xs" value={proposal.street}
                    onChange={(e) => setProposalField("street", e.target.value)} />
                </label>
                <label className="block">
                  <span className="section-label">Nr budynku *</span>
                  <input className="input-base mt-0.5 text-xs" value={proposal.building_no}
                    onChange={(e) => setProposalField("building_no", e.target.value)} />
                </label>
                <label className="block">
                  <span className="section-label">Kod pocztowy *</span>
                  <input className="input-base mt-0.5 text-xs font-mono" placeholder="00-000"
                    value={proposal.postal_code}
                    onChange={(e) => setProposalField("postal_code", e.target.value)} />
                </label>
                <label className="block">
                  <span className="section-label">Miejscowość *</span>
                  <input className="input-base mt-0.5 text-xs" value={proposal.city}
                    onChange={(e) => setProposalField("city", e.target.value)} />
                </label>
                <label className="block">
                  <span className="section-label">Telefon</span>
                  <input className="input-base mt-0.5 text-xs" value={proposal.phone}
                    onChange={(e) => setProposalField("phone", e.target.value)} />
                </label>
                <label className="block">
                  <span className="section-label">E-mail</span>
                  <input className="input-base mt-0.5 text-xs" value={proposal.email}
                    onChange={(e) => setProposalField("email", e.target.value)} />
                </label>
                <label className="block col-span-2">
                  <span className="section-label">
                    Numer konta (opłata skarbowa za wypis i wyrys)
                  </span>
                  <input className="input-base mt-0.5 text-xs font-mono" placeholder="26 cyfr (PL…)"
                    value={proposal.bank_account}
                    onChange={(e) => setProposalField("bank_account", e.target.value)} />
                  {/* Osobny link do źródła KONTA — adres i numer rachunku pochodzą
                      zwykle z różnych podstron, więc jeden link nie wystarcza. */}
                  {proposal.bank_account_source && (
                    <a href={proposal.bank_account_source} target="_blank" rel="noreferrer"
                       title={proposal.bank_account_source}
                       className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline">
                      <ExternalLink size={11} /> Źródło numeru konta
                    </a>
                  )}
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => setProposal(null)}>
                  Odrzuć
                </Button>
                <Button
                  size="sm"
                  loading={acceptProposal.isPending}
                  disabled={
                    !proposal.name || !proposal.street || !proposal.building_no ||
                    !proposal.postal_code || !proposal.city
                  }
                  onClick={() => acceptProposal.mutate()}
                >
                  Zapisz w rejestrze i wybierz
                </Button>
              </div>
            </div>
          )}
          {lookupError && <div className="text-[11px] text-red-600 mt-1">{lookupError}</div>}

          <p className="text-[11px] text-[var(--color-text-muted)] mt-1">
            Opłata skarbowa za pełnomocnictwo (17 zł) idzie do tego samego urzędu.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label-base">Wnioskodawca</label>
            <select
              className="input-base"
              value={applicant}
              onChange={(e) => setApplicant(e.target.value as WypisApplicant)}
            >
              {APPLICANT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-base">Osoba do kontaktu</label>
            <select
              className="input-base"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
            >
              <option value="">—</option>
              {(users ?? []).map((u) => (
                <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="label-base">Inne informacje (wchodzą w treść wniosku)</label>
          <textarea
            className="input-base"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {/* ── ostrzeżenia, nie blokady ── */}
        {withoutPlan.length > 0 && (
          <div className="flex gap-2 border border-amber-300 bg-amber-50/60 p-2 text-xs text-amber-900">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>
              {withoutPlan.length} z {selected.length} działek nie ma potwierdzonego pokrycia
              planem miejscowym. Możesz je zamówić — gmina wyda wtedy zaświadczenie o braku
              planu miejscowego.
            </span>
          </div>
        )}
        {missingAddress.length > 0 && (
          <div className="flex gap-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2 text-xs">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>
              {missingAddress.length} działek nie ma jeszcze miejscowości ani adresu — w tabeli
              wniosku wyjdą jako „obr. …”. Uzupełnisz je przyciskiem „Uzupełnij adresy”.
            </span>
          </div>
        )}

        {/* ── działki ── */}
        <div>
          <div className="text-xs font-mono uppercase tracking-wide mb-1">
            Działki ({selected.length})
          </div>
          <div className="max-h-56 overflow-y-auto border border-[var(--color-border)]">
            <table className="w-full text-xs">
              <tbody>
                {selected.map((p) => (
                  <tr key={p.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="px-2 py-1 font-mono">{p.short_number || p.teryt}</td>
                    <td className="px-2 py-1">{p.obreb || "—"}</td>
                    <td className="px-2 py-1">
                      <MpzpCoverageBadge status={p.mpzp_status} label={p.mpzp_status_label} />
                    </td>
                    <td className="px-2 py-1 text-[var(--color-text-muted)]">
                      {p.reasons.map((r) => r.label).join(", ") || "—"}
                    </td>
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        className="text-[var(--color-text-muted)] hover:text-red-600"
                        title="Usuń z zamówienia"
                        onClick={() => setSelected((s) => s.filter((x) => x.id !== p.id))}
                      >
                        <X size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {error && <div className="text-xs text-red-600">{error}</div>}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Anuluj</Button>
          <Button onClick={submit} loading={create.isPending}>
            Utwórz zamówienie
          </Button>
        </div>
      </div>
    </Modal>
  );
}
